import {
  ClientSecret,
  ClientSecretWithValue,
  RemitSummary,
} from "api/account/client";
import Button from "components/components/Button/Button";
import LoadingSpinner from "components/components/LoadingSpinner/LoadingSpinner";
import Modal, { ModalSize } from "components/components/Modal/Modal";
import { RemitPageFrame } from "components/components/Remit/commonStyle";
import WarningContainer from "components/components/WarningContainer/WarningContainer";
import ClientDetails from "components/shared/ClientDetails/ClientDetails";
import ClientSecretModal from "components/shared/ClientSecretModal/ClientSecretModal";
import { LoadingSpinnerContainer } from "components/shared/ClientSecretModal/style";
import SecretsTable from "components/shared/SecretsTable/SecretsTable";
import { RequestStatus } from "hooks/useRequest";
import React, { useEffect, useMemo, useState } from "react";

import {
  ButtonsDiv,
  ClientDetailsContainer,
  ContentContainer,
  ContentDiv,
  RequestSecretButton,
  SubsectionHeader,
  StyledCancelButton,
} from "./style";

type Props = {
  data: RemitSummary;
  createdSecret: ClientSecretWithValue | null;
  fetchCredentials: () => Promise<void>;
  fetchCredentialsStatus: RequestStatus;
  requestNewSecret: () => Promise<void>;
  requestNewSecretStatus: RequestStatus;
  deleteSecret: (secretId: string) => Promise<void>;
  deleteSecretStatus: RequestStatus;
};

const EXPIRED_SECRET_TOOLTIP =
  "Request a new secret and replace it in your client to continue using REMIT API.";
const EXPIRING_SOON_SECRET_TOOLTIP =
  "Request a new secret before this secret expires and replace it " +
  "in your client to ensure you continue using REMIT API.";

const RemitApiCredentialsDetails: React.FC<Props> = ({
  data,
  createdSecret,
  fetchCredentials,
  fetchCredentialsStatus,
  requestNewSecret,
  requestNewSecretStatus,
  deleteSecret,
  deleteSecretStatus,
}) => {
  const [secretToDelete, setSecretToDelete] = useState<ClientSecret | null>(
    null
  );
  const [isSecretModalOpen, setIsSecretModalOpen] = useState(false);
  const [dismissedSecretId, setDismissedSecretId] = useState<string | null>(
    null
  );
  const isMutationInProgress =
    fetchCredentialsStatus === RequestStatus.IN_PROGRESS ||
    requestNewSecretStatus === RequestStatus.IN_PROGRESS ||
    deleteSecretStatus === RequestStatus.IN_PROGRESS;
  const isRequestingSecret =
    requestNewSecretStatus === RequestStatus.IN_PROGRESS;

  const buttonsDisabled = useMemo(
    () => isMutationInProgress,
    [isMutationInProgress]
  );

  useEffect(() => {
    if (
      isRequestingSecret ||
      (createdSecret && createdSecret.secretId !== dismissedSecretId)
    ) {
      setIsSecretModalOpen(true);
    }
  }, [createdSecret, dismissedSecretId, isRequestingSecret]);

  const requestSecret = async (): Promise<void> => {
    await requestNewSecret();
    await fetchCredentials();
  };

  const removeSecret = async (): Promise<void> => {
    if (!secretToDelete) {
      return;
    }

    await deleteSecret(secretToDelete.secretId);
    await fetchCredentials();
    setSecretToDelete(null);
  };

  const closeSecretModal = (): void => {
    if (isRequestingSecret) {
      return;
    }

    setDismissedSecretId(createdSecret?.secretId ?? null);
    setIsSecretModalOpen(false);
  };

  return (
    <>
      <SubsectionHeader>Submit a REMIT Notification via API</SubsectionHeader>
      <RemitPageFrame>
        <ClientDetailsContainer>
          <ClientDetails
            items={[
              { label: "Client ID", value: data.appClientId },
              {
                label: "API endpoint",
                value: `${process.env.REACT_APP_ACCOUNT_API_BASE_URL}/remit/submit-api`,
              },
            ]}
          />
          <SecretsTable
            applicationSecrets={data.applicationSecrets}
            deleteDisabled={buttonsDisabled}
            setSecretToDelete={setSecretToDelete}
            expiredTooltipText={EXPIRED_SECRET_TOOLTIP}
            expiringSoonTooltipText={EXPIRING_SOON_SECRET_TOOLTIP}
          />
          <ContentDiv aria-live="polite">
            <p>
              You can request a new secret at any time and each secret is valid
              2 years. Once generated, a secret cannot be retrieved. If you have
              lost your secret or require a new one, please{" "}
              <RequestSecretButton
                type="button"
                onClick={requestSecret}
                disabled={buttonsDisabled}
                className="is-link"
              >
                <b>request a new secret</b>
              </RequestSecretButton>
              .
            </p>
          </ContentDiv>
        </ClientDetailsContainer>
      </RemitPageFrame>

      <ClientSecretModal
        title="Your new client secret"
        secret={isSecretModalOpen ? createdSecret : null}
        isActive={isSecretModalOpen}
        isLoading={isRequestingSecret}
        close={closeSecretModal}
      />
      <Modal
        size={ModalSize.medium}
        isActive={secretToDelete !== null}
        onClose={(): void => setSecretToDelete(null)}
      >
        <div aria-live="polite">
          {deleteSecretStatus === RequestStatus.IN_PROGRESS ? (
            <LoadingSpinnerContainer>
              <LoadingSpinner isLoading />
              <p>Deleting secret...</p>
            </LoadingSpinnerContainer>
          ) : (
            <ContentContainer>
              <h2>Are you sure you want to delete this secret?</h2>
              <WarningContainer>
                <p>
                  Please ensure secret{" "}
                  <b>{secretToDelete?.secretHint.padEnd(18, "*")}</b> is not one
                  you are currently using in your client.
                </p>
              </WarningContainer>
              <hr />
              <ButtonsDiv>
                <StyledCancelButton
                  ariaLabel="Cancel"
                  buttonText="Cancel"
                  onClick={(): void => setSecretToDelete(null)}
                  disabled={isMutationInProgress}
                />
                <Button
                  ariaLabel="Delete secret"
                  buttonText="Delete secret"
                  onClick={removeSecret}
                  disabled={isMutationInProgress}
                />
              </ButtonsDiv>
            </ContentContainer>
          )}
        </div>
      </Modal>
    </>
  );
};

export default RemitApiCredentialsDetails;
